import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  MEAL_SLOTS,
  SUPPLEMENT_FORMS,
  describeAmount,
  isPackageBarcode,
  unitFitsForm,
  unitLabel,
  unitsFor,
} from '@kraftwerk/shared';
import type { MealSlot, SupplementForm } from '@kraftwerk/shared';
import { ApiError, apiFetch } from '../../api.js';
import { db } from '../../db/local.js';
import type { LocalSupplement } from '../../db/local.js';
import {
  createSupplement,
  deleteSupplement,
  listSupplements,
  supplementByBarcode,
  updateSupplement,
} from '../../db/supplements.js';
import type { SupplementDraft } from '../../db/supplements.js';
import { BarcodeScanner } from './BarcodeScanner.js';
import { toNumber } from './FoodForm.js';
import type { FoodSuggestion } from './FoodLookup.js';

/**
 * Supplements in der Bibliothek (16.10.2026) — neben Gerichten und
 * Lebensmitteln, weil man sie dort einrichtet; abgehakt wird im Tag und auf
 * „Heute“.
 *
 * Je Supplement: Form (Pulver, Kapsel/Tablette, Öl/Flüssiges) mit passender
 * Einheit, die Menge je Einnahme von der Packung, der Plan (Uhrzeiten,
 * Wochentage, zur Mahlzeit) und der Strichcode — ein späterer Scan findet es
 * wieder. Werte schlägt die App keine vor.
 */
export function SupplementLibrary(): React.JSX.Element {
  const { t } = useTranslation();
  const supplements = useLiveQuery(() => listSupplements(db), [], [] as LocalSupplement[]);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState<Partial<SupplementDraft> | null>(null);
  const [scanning, setScanning] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const scanned = async (code: string) => {
    setScanning(false);
    setNotice(null);
    const known = await supplementByBarcode(code, db);
    if (known) {
      setCreating(null);
      setEditing(known.id);
      setNotice(t('supplementLib.scanKnown', { name: known.name }));
      return;
    }
    let name = '';
    try {
      const body = await apiFetch<{ suggestions: FoodSuggestion[] }>(
        `/foods/lookup?q=${encodeURIComponent(code)}`,
      );
      const hit = body.suggestions[0];
      if (hit) name = hit.brand ? `${hit.name} (${hit.brand})` : hit.name;
    } catch (error) {
      // Offline oder Open Food Facts nicht erreichbar: Der Code bleibt trotzdem.
      if (error instanceof ApiError && error.status === 429) {
        setNotice(t('supplementLib.scanTooMany'));
      }
    }
    setEditing(null);
    setCreating({ name, barcode: code, sourceNote: t('supplementLib.sourcePackage') });
    setNotice(name ? t('supplementLib.scanFound') : t('supplementLib.scanUnknown'));
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-ink-muted">{t('supplementLib.intro')}</p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => {
            setNotice(null);
            setEditing(null);
            setCreating({ sourceNote: t('supplementLib.sourcePackage') });
          }}
          className="kw-btn kw-btn-small flex-1"
        >
          {t('supplementLib.new')}
        </button>
        <button
          type="button"
          onClick={() => setScanning((value) => !value)}
          aria-expanded={scanning}
          className="kw-btn kw-btn-quiet kw-btn-small flex-1"
        >
          {t('supplementLib.scan')}
        </button>
      </div>

      {scanning ? (
        <BarcodeScanner onCode={(code) => void scanned(code)} onClose={() => setScanning(false)} />
      ) : null}

      {notice ? (
        <p role="status" className="text-sm text-ink-muted">
          {notice}
        </p>
      ) : null}

      {creating ? (
        <SupplementForm
          initial={creating}
          onSave={(draft) => void createSupplement(draft, db).then(() => setCreating(null))}
          onCancel={() => setCreating(null)}
        />
      ) : null}

      {supplements.length === 0 ? (
        <p className="text-sm text-ink-muted">{t('supplements.empty')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {supplements.map((entry) => (
            <li key={entry.id} className="kw-tile">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-medium">{entry.name}</p>
                  <p className="text-sm text-ink-muted">
                    <SupplementSummary supplement={entry} />
                  </p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => setEditing(editing === entry.id ? null : entry.id)}
                    aria-expanded={editing === entry.id}
                    aria-label={t('supplementLib.editNamed', { name: entry.name })}
                    className="kw-btn kw-btn-quiet kw-btn-small px-3"
                  >
                    {t('nutrition.library.edit')}
                  </button>
                  <button
                    type="button"
                    onClick={() => void deleteSupplement(entry.id)}
                    aria-label={t('supplements.removeNamed', { name: entry.name })}
                    className="kw-btn kw-btn-quiet kw-btn-small px-3 hover:border-danger hover:text-danger"
                  >
                    {t('supplements.remove')}
                  </button>
                </div>
              </div>
              {editing === entry.id ? (
                <div className="mt-3">
                  <SupplementForm
                    initial={entry}
                    onSave={(draft) =>
                      void updateSupplement(entry.id, draft, db).then(() => setEditing(null))
                    }
                    onCancel={() => setEditing(null)}
                  />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** „1 Stück · 08:00, 20:00 · Mo, Do · zum Frühstück“ — fehlt etwas, steht es da. */
export function SupplementSummary({
  supplement,
}: {
  supplement: LocalSupplement;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const language = i18n.language.startsWith('en') ? 'en' : 'de';
  const amount = describeAmount(supplement.dose, supplement.unit, language);
  const parts = [
    amount || t('supplementLib.noAmount'),
    supplement.scheduleTimes.length > 0
      ? supplement.scheduleTimes.join(', ')
      : t('supplements.noTime'),
    supplement.weekdays.length > 0
      ? supplement.weekdays.map((day) => t(`supplementLib.weekday.${day}`)).join(', ')
      : t('supplementLib.daily'),
    supplement.mealSlot
      ? t('supplements.atMeal', { slot: t(`nutrition.slot.${supplement.mealSlot}`) })
      : null,
  ].filter((part): part is string => part !== null);
  return <>{parts.join(' · ')}</>;
}

function SupplementForm({
  initial,
  onSave,
  onCancel,
}: {
  initial: Partial<SupplementDraft> | LocalSupplement;
  onSave: (draft: SupplementDraft) => void;
  onCancel: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const language = i18n.language.startsWith('en') ? 'en' : 'de';
  const ids = {
    name: useId(),
    amount: useId(),
    unit: useId(),
    note: useId(),
    meal: useId(),
    barcode: useId(),
    source: useId(),
  };
  const [name, setName] = useState(initial.name ?? '');
  const [form, setForm] = useState<SupplementForm | null>(initial.form ?? null);
  const [amount, setAmount] = useState(
    initial.dose === null || initial.dose === undefined ? '' : String(initial.dose),
  );
  const [unit, setUnit] = useState<string | null>(initial.unit ?? null);
  const [times, setTimes] = useState<string[]>(
    initial.scheduleTimes && initial.scheduleTimes.length > 0 ? initial.scheduleTimes : ['08:00'],
  );
  const [weekdays, setWeekdays] = useState<number[]>(initial.weekdays ?? []);
  const [meal, setMeal] = useState<MealSlot | ''>(initial.mealSlot ?? '');
  const [note, setNote] = useState(initial.note ?? '');
  const [barcode, setBarcode] = useState(initial.barcode ?? '');
  const [source, setSource] = useState(initial.sourceNote ?? '');
  const [problem, setProblem] = useState<string | null>(null);

  const pickForm = (next: SupplementForm) => {
    setForm(next);
    // Eine Kapsel in Gramm gibt es nicht: Passt die Einheit nicht, gilt die
    // erste der neuen Form.
    // Ohne Einheit galt sonst nur die Anzeige — gespeichert wäre keine gewesen.
    if (unit === null || !unitFitsForm(next, unit)) setUnit(unitsFor(next)[0] ?? null);
  };
  const unitOptions = form ? unitsFor(form) : [];

  return (
    <form
      className="flex flex-col gap-3 rounded-xl border border-accent bg-surface-raised p-3"
      onSubmit={(event) => {
        event.preventDefault();
        setProblem(null);
        const dose = amount.trim() === '' ? null : toNumber(amount);
        if (amount.trim() !== '' && dose === null) {
          setProblem(t('supplementLib.amountInvalid'));
          return;
        }
        if (barcode.trim() !== '' && !isPackageBarcode(barcode.trim())) {
          setProblem(t('supplementLib.barcodeInvalid'));
          return;
        }
        onSave({
          name: name.trim(),
          form,
          dose,
          unit: dose === null ? null : unit,
          scheduleTimes: [...new Set(times.filter((time) => time !== ''))].sort(),
          weekdays: [...weekdays].sort(),
          mealSlot: meal === '' ? null : meal,
          note: note.trim() || null,
          barcode: barcode.trim() || null,
          sourceNote: source.trim() || null,
        });
      }}
    >
      <label htmlFor={ids.name} className="kw-label flex flex-col gap-1.5">
        {t('supplements.name')}
        <input
          id={ids.name}
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          maxLength={120}
          className="kw-field"
        />
      </label>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="kw-label">{t('supplementLib.form')}</legend>
        <div className="flex flex-wrap gap-1">
          {SUPPLEMENT_FORMS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={form === option}
              onClick={() => pickForm(option)}
              className="kw-toggle"
            >
              {t(`supplementLib.forms.${option}`)}
            </button>
          ))}
        </div>
      </fieldset>

      <div className="flex flex-wrap items-end gap-2">
        <label htmlFor={ids.amount} className="kw-label flex w-24 flex-col gap-1.5">
          {t('supplementLib.amount')}
          <input
            id={ids.amount}
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            className="kw-field"
          />
        </label>
        {form ? (
          <label htmlFor={ids.unit} className="kw-label flex min-w-32 flex-1 flex-col gap-1.5">
            {t('supplementLib.unit')}
            <select
              id={ids.unit}
              value={unit ?? unitOptions[0] ?? ''}
              onChange={(event) => setUnit(event.target.value)}
              className="kw-field"
            >
              {unitOptions.map((option) => (
                <option key={option} value={option}>
                  {unitLabel(option, language, 2)}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <p className="flex-1 text-xs text-ink-muted">{t('supplementLib.pickFormFirst')}</p>
        )}
      </div>

      <label htmlFor={ids.note} className="kw-label flex flex-col gap-1.5">
        {t('supplementLib.content')}
        <input
          id={ids.note}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={t('supplementLib.contentExample')}
          maxLength={200}
          className="kw-field"
        />
      </label>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="kw-label">{t('supplementLib.times')}</legend>
        {times.map((time, index) => (
          <div key={index} className="flex items-center gap-2">
            <input
              type="time"
              value={time}
              aria-label={t('supplementLib.timeNumber', { number: index + 1 })}
              onChange={(event) =>
                setTimes(times.map((entry, at) => (at === index ? event.target.value : entry)))
              }
              className="kw-field w-32"
            />
            {times.length > 1 ? (
              <button
                type="button"
                onClick={() => setTimes(times.filter((_, at) => at !== index))}
                aria-label={t('supplementLib.removeTime', { time })}
                className="kw-btn kw-btn-quiet kw-btn-small px-3"
              >
                ×
              </button>
            ) : null}
          </div>
        ))}
        {times.length < 6 ? (
          <button
            type="button"
            onClick={() => setTimes([...times, '20:00'])}
            className="self-start py-2 text-sm text-ink-muted underline underline-offset-2"
          >
            {t('supplementLib.addTime')}
          </button>
        ) : null}
      </fieldset>

      <fieldset className="flex flex-col gap-1.5">
        <legend className="kw-label">{t('supplementLib.weekdays')}</legend>
        <div className="flex flex-wrap gap-1">
          {[1, 2, 3, 4, 5, 6, 7].map((day) => (
            <button
              key={day}
              type="button"
              aria-pressed={weekdays.includes(day)}
              onClick={() =>
                setWeekdays(
                  weekdays.includes(day)
                    ? weekdays.filter((entry) => entry !== day)
                    : [...weekdays, day],
                )
              }
              className="kw-toggle"
            >
              {t(`supplementLib.weekday.${day}`)}
            </button>
          ))}
        </div>
        <p className="text-xs text-ink-muted">
          {weekdays.length === 0 ? t('supplementLib.dailyHint') : null}
        </p>
      </fieldset>

      <label htmlFor={ids.meal} className="kw-label flex flex-col gap-1.5">
        {t('supplements.withMeal')}
        <select
          id={ids.meal}
          value={meal}
          onChange={(event) => setMeal(event.target.value as MealSlot | '')}
          className="kw-field"
        >
          <option value="">{t('supplements.noMeal')}</option>
          {MEAL_SLOTS.map((option) => (
            <option key={option} value={option}>
              {t('supplements.atMeal', { slot: t(`nutrition.slot.${option}`) })}
            </option>
          ))}
        </select>
      </label>

      <div className="flex flex-wrap gap-2">
        <label htmlFor={ids.barcode} className="kw-label flex min-w-40 flex-1 flex-col gap-1.5">
          {t('supplementLib.barcode')}
          <input
            id={ids.barcode}
            inputMode="numeric"
            value={barcode}
            onChange={(event) => setBarcode(event.target.value)}
            maxLength={14}
            className="kw-field"
          />
        </label>
        <label htmlFor={ids.source} className="kw-label flex min-w-40 flex-1 flex-col gap-1.5">
          {t('supplementLib.source')}
          <input
            id={ids.source}
            value={source}
            onChange={(event) => setSource(event.target.value)}
            maxLength={200}
            className="kw-field"
          />
        </label>
      </div>

      {problem ? (
        <p role="alert" className="text-xs text-danger">
          {problem}
        </p>
      ) : null}

      <div className="flex gap-2">
        <button type="submit" className="kw-btn flex-1">
          {t('supplementLib.save')}
        </button>
        <button type="button" onClick={onCancel} className="kw-btn kw-btn-quiet basis-2/5">
          {t('nutrition.library.cancel')}
        </button>
      </div>
    </form>
  );
}
